import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import SectionParticles from './SectionParticles';

const AboutSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const fadeUpVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <section 
      id="about" 
      className="py-24 md:py-32 relative overflow-hidden" 
      ref={ref} 
      data-testid="about-section"
      style={{
        background: 'linear-gradient(180deg, #050505 0%, #0a1225 50%, #050505 100%)'
      }}
    >
      <SectionParticles />
      <div className="max-w-6xl mx-auto px-6 md:px-12 relative z-10">
        {/* Section divider */}
        <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-[#c9a227]/30 to-transparent mb-16" />
        
        <motion.div
          variants={fadeUpVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          transition={{ duration: 0.8 }}
          className="mb-12 text-center"
        >
          <h2 
            className="text-3xl md:text-4xl lg:text-5xl font-light tracking-[0.2em] text-white mb-4"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            About
          </h2>
          <div className="w-24 h-[1px] bg-[#c9a227]/50 mx-auto" />
        </motion.div>

        <div className="grid md:grid-cols-2 gap-12 md:gap-16">
          <motion.div
            variants={fadeUpVariants}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h3 
              className="text-xl md:text-2xl font-light tracking-wider text-[#c9a227] mb-6"
              style={{ fontFamily: "'Cinzel', serif" }}
            >
              About Celestial Minds™
            </h3>
            <div className="space-y-6 text-white/70 leading-relaxed">
              <p>
                Celestial Minds™ was created to operate upstream of conventional institutions, markets, and industries — in the space where new systems are conceived before they become visible, viable, or inevitable.
              </p>
              <p>
                We exist to originate — not merely analyze — next-generation systems, platforms, and frameworks capable of shaping the future of technology, finance, infrastructure, intelligence, and human experience. Our work focuses on first-principles design: identifying structural gaps in the world and architecting solutions that can exist independently of prevailing trends, incentives, or cycles.
              </p>
              <p>
                Unlike traditional organizations, Celestial Minds™ does not pursue scale for its own sake, nor do we publish ideas prematurely. We do not measure progress by volume, visibility, or speed. Ideas are developed through deep synthesis, structural clarity, and long-term thinking until they reach a level of coherence suitable for real-world deployment, institutional adoption, or strategic partnership.
              </p>
            </div>
          </motion.div>

          <motion.div
            variants={fadeUpVariants}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <div className="space-y-6 text-white/70 leading-relaxed">
              <p>
                Engagements are selective and discreet. We collaborate only where there is clear alignment, shared long-term vision, and respect for intellectual sovereignty. Some initiatives evolve into ventures or partnerships; others remain foundational frameworks that inform future systems and decisions. Not all work is publicly disclosed.
              </p>
              <p>
                Celestial Minds™ is intentionally designed to remain focused, independent, and uncompromised — preserving coherence, originality, and strategic depth over time. We are built to think beyond immediate cycles and to create work that remains relevant as conditions change.
              </p>
              <blockquote 
                className="border-l-2 border-[#c9a227]/50 pl-6 py-4 my-8 text-white/90 italic text-lg"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                "Celestial Minds FZE designs the systems that future societies will rely on — before they realise they need them."
              </blockquote>
            </div>
          </motion.div>
        </div>

        {/* Founder's Vision */}
        <motion.div
          variants={fadeUpVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-20 md:mt-28"
        >
          <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-[#c9a227]/30 to-transparent mb-12" />
          <h3 
            className="text-xl md:text-2xl font-light tracking-wider text-[#c9a227] mb-8 text-center"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            Our founder's vision
          </h3>
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-white/60 leading-relaxed mb-6">
              The foundation of Celestial Minds was built on one powerful question:
            </p>
            <blockquote 
              className="text-2xl md:text-3xl font-light text-[#c9a227] mb-6"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              "What if human civilisation could operate at its highest possible level?"
            </blockquote>
            <p className="text-white/60 leading-relaxed">
              Everything we create is an attempt to answer that question — beautifully, intelligently, and fearlessly.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutSection;
